import React, { useEffect, useState, useRef } from 'react';
import '../../style/filePreview.css';
import { ERROR } from '../../helpers/constants';
import { Alert } from 'arag-ui-lib';

const SUPPORTED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.jpe', '.jfif', '.pjp', '.pjpeg', '.png', '.bmp', '.gif', '.webp', '.svg', '.txt'];

const getFileExtension = (filename) => {
    const lastDot = filename?.lastIndexOf('.') ?? -1;
    return lastDot > -1 ? filename.slice(lastDot).toLowerCase() : '';
};

const base64ToBlob = (base64, mimeType) => {
    const byteCharacters = atob(base64);
    const byteNumbers = Uint8Array.from(byteCharacters, char => char.charCodeAt(0));
    return new Blob([byteNumbers], { type: mimeType });
};

const MIME_TYPES = {
    '.pdf': 'application/pdf',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.jpe': 'image/jpeg',
    '.jfif': 'image/jpeg',
    '.pjp': 'image/jpeg',
    '.pjpeg': 'image/jpeg',
    '.png': 'image/png',
    '.bmp': 'image/bmp',
    '.gif': 'image/gif',
    '.webp': 'image/webp',
    '.svg': 'image/svg+xml',
    '.txt': 'text/plain',
};

// Helper to create image preview handler
const createImageHandler = (mimeType) => ({
    render: ({ base64 }) => (
        <img
            src={`data:${mimeType};base64,${base64}`}
            alt="Preview"
            className="file-preview__image"
        />
    )
});

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.jpe', '.jfif', '.pjp', '.pjpeg', '.png', '.bmp', '.gif', '.webp', '.svg'];

const PREVIEW_HANDLERS = {
    '.pdf': {
        setup: ({ base64, setObjectUrl }) => {
            const blob = base64ToBlob(base64, MIME_TYPES['.pdf']);
            const url = URL.createObjectURL(blob);
            setObjectUrl(url);
            return () => URL.revokeObjectURL(url);
        },
        render: ({ objectUrl }) => (
            <iframe
                src={objectUrl}
                className="file-preview__iframe"
                title="PDF Preview"
            />
        )
    },
    ...IMAGE_EXTENSIONS.reduce((handlers, ext) => {
        handlers[ext] = createImageHandler(MIME_TYPES[ext]);
        return handlers;
    }, {}),
    '.txt': {
        setup: ({ base64, setTextContent }) => {
            const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
            const text = new TextDecoder('utf-8').decode(bytes);
            setTextContent(text);
        },
        render: ({ textContent }) => (
            <pre className="file-preview__text">{textContent}</pre>
        )
    },
    default: {
        render: ({ fileName }) => (
            <div className="file-preview__unsupported">
                <p>Preview not available for this file type</p>
                <p className="file-preview__filename">{fileName}</p>
            </div>
        )
    }
};

export const isPreviewable = (filename) => {
    const ext = getFileExtension(filename);
    return SUPPORTED_EXTENSIONS.includes(ext);
};

const FilePreview = ({ base64, fileName }) => {
    const [objectUrl, setObjectUrl] = useState(null);
    const [textContent, setTextContent] = useState(null);
    const [error, setError] = useState(null);
    const containerRef = useRef(null);

    const extension = getFileExtension(fileName);
    const handler = PREVIEW_HANDLERS[extension] || PREVIEW_HANDLERS.default;

    useEffect(() => {
        let cleanup = null;
        let isMounted = true;

        const runSetup = async () => {
            try {
                setError(null);
                const result = handler.setup?.({
                    base64,
                    setObjectUrl,
                    setTextContent,
                    containerRef
                });

                cleanup = await result;
            } catch (err) {
                if (isMounted) {
                    setError('Failed to load preview');
                    console.error('FilePreview setup error:', err);
                }
            }
        };

        base64 && runSetup();

        return () => {
            isMounted = false;
            cleanup?.();
        };
    }, [base64, extension]);

    const onDismiss = () => {
        setError(null);
    };

    return (
        <div className="file-preview" ref={containerRef}>
            {error && <div className='col-sm-12'>
                <Alert label={error} variant={ERROR} onDismiss={onDismiss}  />
            </div>}
            {!error && handler.render({
                objectUrl,
                textContent,
                base64,
                fileName
            })}
        </div>
    );
};

export default FilePreview;
